<template>
    <el-dialog v-model="visible" width="min(92vw, 620px)" class="feedback-submit-dialog"
        :title="t('feedback.entry_title')" :close-on-click-modal="false" :before-close="handleBeforeClose" draggable
        overflow append-to-body destroy-on-close>

        <el-form ref="formRef" :model="form" :rules="rules" label-position="top" status-icon>
            <el-form-item :label="t('feedback.title')" prop="categoryKey">
                <el-select v-model="form.categoryKey" class="feedback-category-select"
                    :placeholder="t('feedback.category_placeholder')">
                    <el-option v-for="option in feedbackOptions" :key="option.key" :label="option.label"
                        :value="option.key" />
                </el-select>
            </el-form-item>

            <el-form-item :label="t('feedback.content')" prop="content">
                <el-input v-model="form.content" type="textarea" :rows="6" resize="none" :maxlength="2000"
                    show-word-limit :placeholder="t('feedback.content_placeholder')" />
            </el-form-item>

            <el-form-item :label="t('feedback.attachments')">
                <el-upload v-model:file-list="fileList" class="feedback-image-upload" accept="image/*"
                    list-type="picture-card" :limit="9" :before-upload="beforeUpload" :http-request="uploadImage"
                    :on-remove="removeImage" :on-exceed="handleExceed">
                    <div class="feedback-upload-trigger">
                        <el-icon>
                            <Camera />
                        </el-icon>
                        <span>{{ t('feedback.upload_image') }}</span>
                    </div>
                </el-upload>
                <div class="feedback-upload-tip">{{ t('feedback.upload_tip') }}</div>
            </el-form-item>
        </el-form>

        <template #footer>
            <div class="feedback-dialog-footer">
                <el-button class="management-action-button" plain size="small" :icon="DocumentChecked"
                    :disabled="submitting" @click="goToMyFeedback">
                    {{ t('feedback.list.entry') }}
                </el-button>
                <div class="feedback-dialog-actions">
                    <el-button :disabled="submitting" @click="closeDialog">{{ t('button.cancel') }}</el-button>
                    <el-button type="primary" :loading="submitting" :disabled="uploadingCount > 0"
                        @click="submitFeedback">
                        {{ t('feedback.submit') }}
                    </el-button>
                </div>
            </div>
        </template>
    </el-dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { Camera, DocumentChecked } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { FeedbackControllerApi } from '@/http/apis/FeedbackControllerApi'
import { UploadFilesControllerApi } from '@/http/apis/UploadFilesControllerApi'

const { t } = useI18n()
const router = useRouter()
const feedbackApi = new FeedbackControllerApi()
const uploadApi = new UploadFilesControllerApi()

const visible = ref(false)
const formRef = ref(null)
const fileList = ref([])
const uploadedFilesByUid = new Map()
const submitting = ref(false)
const uploadingCount = ref(0)
const submitted = ref(false)

const feedbackOptions = computed(() => [
    { key: 'functional_error', type: 1, label: t('feedback.categories.functional_error') },
    { key: 'display_error', type: 1, label: t('feedback.categories.display_error') },
    { key: 'order_problem', type: 1, label: t('feedback.categories.order_problem') },
    { key: 'payment_refund_problem', type: 1, label: t('feedback.categories.payment_refund_problem') },
    { key: 'data_error', type: 1, label: t('feedback.categories.data_error') },
    { key: 'functional_improvement', type: 2, label: t('feedback.categories.functional_improvement') },
    { key: 'new_feature', type: 2, label: t('feedback.categories.new_feature') },
    { key: 'usability_improvement', type: 2, label: t('feedback.categories.usability_improvement') },
    { key: 'other', type: 2, label: t('feedback.categories.other') },
])

const form = reactive({
    categoryKey: '',
    content: '',
})

const selectedCategory = computed(() => feedbackOptions.value.find(option => option.key === form.categoryKey))
const feedbackType = computed(() => selectedCategory.value?.type || 1)
const categoryLabel = computed(() => selectedCategory.value?.label || '')

const rules = computed(() => ({
    categoryKey: [
        { required: true, message: t('feedback.title_required'), trigger: 'change' },
    ],
    content: [
        { required: true, whitespace: true, message: t('feedback.content_required'), trigger: 'blur' },
        { max: 2000, message: t('feedback.content_limit'), trigger: 'blur' },
    ],
}))

const resetForm = () => {
    form.categoryKey = ''
    form.content = ''
    fileList.value = []
    uploadedFilesByUid.clear()
    formRef.value?.clearValidate()
}

const getUploadedFileIds = () => Array.from(uploadedFilesByUid.values())
    .map(file => file.id)
    .filter(Boolean)

const deleteUploadedFiles = async (ids) => {
    if (!ids.length) return
    await Promise.allSettled(ids.map(id => uploadApi.uploadFilesIdDelete({ id })))
}

const open = (option = {}) => {
    resetForm()
    if (feedbackOptions.value.some(item => item.key === option.key)) {
        form.categoryKey = option.key
    }
    submitted.value = false
    visible.value = true
}

const beforeUpload = (file) => {
    if (!file.type?.startsWith('image/')) {
        ElMessage.error(t('feedback.image_only'))
        return false
    }
    if (file.size / 1024 / 1024 > 10) {
        ElMessage.error(t('feedback.image_size_limit'))
        return false
    }
    return true
}

const uploadImage = async ({ file, onSuccess, onError }) => {
    uploadingCount.value += 1
    const currentFile = fileList.value.find(item => item.uid === file.uid)
    if (currentFile && !String(currentFile.url || '').startsWith('blob:')) {
        currentFile.url = URL.createObjectURL(file)
    }
    try {
        const res = await uploadApi.uploadFilesUploadPost({
            usageType: 'feedback',
            file,
        })
        if (res.code !== 0 || !res.data?.id) {
            throw new Error(res.message || t('feedback.upload_failed'))
        }
        const uploadResult = {
            id: res.data.id,
            fileUrl: res.data.fileUrl || '',
        }
        uploadedFilesByUid.set(file.uid, uploadResult)
        onSuccess(uploadResult)
    } catch (error) {
        uploadedFilesByUid.delete(file.uid)
        fileList.value = fileList.value.filter(item => item.uid !== file.uid)
        ElMessage.error(error?.message || t('feedback.upload_failed'))
        onError(error)
    } finally {
        uploadingCount.value -= 1
    }
}

const removeImage = async (file) => {
    const id = uploadedFilesByUid.get(file.uid)?.id || file.response?.id
    uploadedFilesByUid.delete(file.uid)
    if (!id) return
    try {
        await uploadApi.uploadFilesIdDelete({ id })
    } catch (error) {
        console.error('[Feedback] 删除附件失败:', error)
    }
}

const handleExceed = () => ElMessage.warning(t('feedback.image_count_limit'))

const closeDialog = async () => {
    if (submitting.value || uploadingCount.value > 0) {
        ElMessage.warning(t('feedback.uploading'))
        return
    }
    const uploadedFileIds = getUploadedFileIds()
    visible.value = false
    resetForm()
    await deleteUploadedFiles(uploadedFileIds)
}

const handleBeforeClose = (done) => {
    if (submitting.value || uploadingCount.value > 0) {
        ElMessage.warning(t('feedback.uploading'))
        return
    }
    const uploadedFileIds = getUploadedFileIds()
    resetForm()
    done()
    if (!submitted.value) deleteUploadedFiles(uploadedFileIds)
}

const goToMyFeedback = async () => {
    if (submitting.value || uploadingCount.value > 0) {
        ElMessage.warning(t('feedback.uploading'))
        return
    }
    const uploadedFileIds = getUploadedFileIds()
    visible.value = false
    resetForm()
    await deleteUploadedFiles(uploadedFileIds)
    router.push('/feedback')
}

const submitFeedback = async () => {
    if (uploadingCount.value > 0) {
        ElMessage.warning(t('feedback.uploading'))
        return
    }
    const valid = await formRef.value?.validate().catch(() => false)
    if (!valid) return

    submitting.value = true
    try {
        const res = await feedbackApi.feedbackSubmitPost({
            submitReqVo: {
                type: feedbackType.value,
                title: categoryLabel.value,
                content: form.content.trim(),
                fileIds: getUploadedFileIds(),
            },
        })
        if (res.code !== 0) throw new Error(res.message || t('feedback.submit_failed'))

        submitted.value = true
        window.dispatchEvent(new CustomEvent('feedback-submitted', {
            detail: { id: res.data?.id },
        }))
        visible.value = false
        resetForm()
        try {
            await ElMessageBox.confirm(t('feedback.success_message'), t('feedback.success_title'), {
                type: 'success',
                confirmButtonText: t('button.confirm'),
                cancelButtonText: t('feedback.list.entry'),
                distinguishCancelAndClose: true,
                showClose: false,
                closeOnClickModal: false,
                closeOnPressEscape: false,
            })
        } catch (action) {
            if (action === 'cancel') router.push('/feedback')
        }
    } catch (error) {
        ElMessage.error(error?.message || t('feedback.submit_failed'))
    } finally {
        submitting.value = false
    }
}

defineExpose({ open })
</script>

<style scoped>
:global(.el-dialog.feedback-submit-dialog) {
    --el-dialog-padding-primary: 0px;
    padding: 0 !important;
    overflow: hidden;
    border-radius: 5px;
}

:global(.feedback-submit-dialog .el-dialog__header) {
    display: flex;
    min-height: 46px;
    align-items: center;
    margin-right: 0;
    padding: 11px 16px;
    border-bottom: 1px solid #dfe5ec;
    background: #eef2f6;
    cursor: move;
    user-select: none;
}

:global(.feedback-submit-dialog .el-dialog__title) {
    color: #25364d;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
}

:global(.feedback-submit-dialog .el-dialog__headerbtn) {
    top: 0;
    right: 0;
    width: 46px;
    height: 46px;
}

:global(.feedback-submit-dialog .el-dialog__close) {
    color: #7d8998;
    font-size: 18px;
}

:global(.feedback-submit-dialog .el-dialog__headerbtn:hover .el-dialog__close) {
    color: #34445a;
}

:global(.feedback-submit-dialog .el-dialog__body) {
    padding: 18px 20px 20px;
}

:global(.feedback-submit-dialog .el-dialog__footer) {
    padding: 11px 16px 14px;
    border-top: 1px solid #edf0f4;
    background: #fff;
}

.feedback-image-upload {
    width: 100%;
}

.feedback-category-select {
    width: 100%;
}

.feedback-image-upload :deep(.el-upload--picture-card),
.feedback-image-upload :deep(.el-upload-list--picture-card .el-upload-list__item) {
    width: 88px;
    height: 88px;
}

.feedback-upload-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    color: #606266;
    font-size: 12px;
}

.feedback-upload-trigger .el-icon {
    font-size: 20px;
}

.feedback-upload-tip {
    width: 100%;
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
}

.feedback-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.feedback-dialog-actions {
    display: flex;
    gap: 8px;
}

.feedback-dialog-actions :deep(.el-button + .el-button) {
    margin-left: 0;
}
</style>
